import { useEffect, useState } from "react";
import { Check, Loader2, X } from "lucide-react";

const Row = ({ label, value, tone = "text-zinc-100", testid }) => (
  <div className="flex items-center justify-between py-2 border-b border-zinc-800/80 last:border-0">
    <span className="text-[11px] uppercase tracking-[0.14em] text-zinc-400 font-semibold">
      {label}
    </span>
    <span className={`font-mono text-sm ${tone}`} data-testid={testid}>
      {value ?? "—"}
    </span>
  </div>
);

/* Mobile two-tap confirmation. Tapping Buy/Sell opens this sheet; Confirm submits. */
export const ConfirmSheet = ({ open, trade, busy, result, onConfirm, onClose }) => {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (open) {
      const id = requestAnimationFrame(() => setShown(true));
      return () => cancelAnimationFrame(id);
    }
    setShown(false);
  }, [open]);

  if (!open || !trade) return null;
  const isBuy = trade.side === "buy";

  return (
    <div className="md:hidden fixed inset-0 z-[60]" data-testid="confirm-sheet">
      <button
        type="button"
        aria-label="Cancel"
        data-testid="confirm-sheet-backdrop"
        onClick={busy ? undefined : onClose}
        className={`absolute inset-0 bg-black/70 transition-opacity duration-200 ${
          shown ? "opacity-100" : "opacity-0"
        }`}
      />
      <div
        className={`absolute inset-x-0 bottom-0 rounded-t-2xl border-t border-zinc-700 bg-[#0c0d0f] px-4 pt-4 pb-6 transition-transform duration-300 ease-out ${
          shown ? "translate-y-0" : "translate-y-full"
        }`}
      >
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-zinc-700" />

        {result?.ok ? (
          <div className="py-8 text-center" data-testid="confirm-success">
            <Check className="mx-auto h-10 w-10 text-emerald-400" />
            <p className="mt-3 font-mono text-sm text-emerald-300">{result.message}</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <span className="font-heading font-bold text-lg tracking-tight text-zinc-50">
                {trade.symbol}
              </span>
              <span
                className={`px-2 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wide ${
                  isBuy ? "bg-emerald-500/25 text-emerald-200" : "bg-red-500/25 text-red-200"
                }`}
                data-testid="confirm-side"
              >
                {trade.side}
              </span>
              <span className="ml-auto text-[10px] uppercase tracking-[0.14em] text-zinc-400 font-semibold">
                {trade.kind}
              </span>
            </div>

            <div className="mt-3">
              <Row label="Lot size" value={trade.volume} tone="text-emerald-300" testid="confirm-volume" />
              {trade.price && <Row label="Order price" value={trade.price} testid="confirm-price" />}
              <Row label="Stop loss" value={trade.sl} tone="text-red-300" testid="confirm-sl" />
              <Row label="TP1" value={trade.tp1} tone="text-emerald-300" testid="confirm-tp1" />
              <Row label="TP2" value={trade.tp2} tone="text-emerald-300" testid="confirm-tp2" />
              <Row
                label="Trailing"
                value={trade.trailing ? `on · ${trade.trailPct}%` : "off"}
                tone={trade.trailing ? "text-emerald-300" : "text-zinc-400"}
                testid="confirm-trailing"
              />
            </div>

            {result?.error && (
              <p
                className="mt-3 rounded-sm border border-red-800/70 bg-red-500/10 px-3 py-2 font-mono text-[11px] leading-snug text-red-300"
                data-testid="confirm-error"
              >
                {result.error}
              </p>
            )}

            <div className="mt-4 flex items-center gap-2">
              <button
                type="button"
                data-testid="confirm-cancel-btn"
                disabled={busy}
                onClick={onClose}
                className="h-12 flex-1 rounded-full border border-zinc-600 text-xs font-bold uppercase tracking-[0.12em] text-zinc-300 transition-colors duration-200 active:scale-95 disabled:opacity-40"
              >
                <X className="mr-1 inline h-4 w-4" /> Cancel
              </button>
              <button
                type="button"
                data-testid="confirm-submit-btn"
                disabled={busy}
                onClick={onConfirm}
                className={`h-12 flex-[1.4] rounded-full text-xs font-bold uppercase tracking-[0.12em] text-white transition-colors duration-200 active:scale-95 disabled:opacity-50 ${
                  isBuy ? "bg-emerald-600" : "bg-red-600"
                }`}
              >
                {busy ? (
                  <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                ) : (
                  `Confirm ${trade.side}`
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
